import { createRoute, lazyRouteComponent, Outlet } from '@tanstack/react-router';
import { z } from 'zod/v3';

import { menuLayoutRoute } from 'root/MenuLayout';
import RequireWarehouse from 'root/RequireWarehouse';

const shrinkageMainRoute = createRoute({
  id: 'shrinkage-main',
  getParentRoute: () => menuLayoutRoute,
});

const ShrinkageRequireWarehouse = () => {
  return (
    <RequireWarehouse
      routeWithWarehouseId="/shrinkage/warehouse/$warehouseId"
      routeWithoutWarehouseId="/shrinkage"
    >
      <Outlet />
    </RequireWarehouse>
  );
};

const requireWarehouseRoute = createRoute({
  getParentRoute: () => shrinkageMainRoute,
  path: '/shrinkage',
  component: ShrinkageRequireWarehouse,
});

const shrinkageWarehouseWrapperRoute = createRoute({
  getParentRoute: () => requireWarehouseRoute,
  path: 'warehouse/$warehouseId',
  parseParams: z.object({ warehouseId: z.string().uuid() }).parse,
});

type ShrinkageViewSearchSchema = { filter?: 'expiringToday' | 'pastExpiry' };

export const shrinkageRoute = createRoute({
  getParentRoute: () => shrinkageWarehouseWrapperRoute,
  path: '/',
  component: lazyRouteComponent(() => import('./ShrinkageView/ShrinkageView')),
  validateSearch: z.custom<ShrinkageViewSearchSchema>().parse,
});

export const removeFromInventoryRoute = createRoute({
  getParentRoute: () => shrinkageWarehouseWrapperRoute,
  path: '/$productId',
  component: lazyRouteComponent(() => import('./RemoveFromInventoryView/RemoveFromInventoryView')),
  parseParams: (params) => ({ productId: z.string().parse(params.productId) }),
});

export const shrinkageBatchesRoute = createRoute({
  getParentRoute: () => shrinkageWarehouseWrapperRoute,
  path: '/$productId/batches',
  component: lazyRouteComponent(() => import('./BatchesView/BatchesView')),
  parseParams: (params) => ({
    productId: z.string().parse(params.productId),
  }),
});

export const removeFromInventoryByBatchRoute = createRoute({
  getParentRoute: () => shrinkageWarehouseWrapperRoute,
  path: '/$productId/batches/$batchId',
  component: lazyRouteComponent(
    () => import('./RemoveFromInventoryViewV2/RemoveFromInventoryViewV2'),
  ),
  parseParams: (params) => ({
    productId: z.string().parse(params.productId),
    batchId: z.string().parse(params.batchId),
  }),
});

export const shrinkItemsTaskRoute = createRoute({
  getParentRoute: () => shrinkageWarehouseWrapperRoute,
  path: '$productId/batches/$batchId/tasks/$taskId',
  component: lazyRouteComponent(
    () => import('./RemoveFromInventoryTaskView/RemoveFromInventoryTaskView'),
  ),
  validateSearch: z.object({
    subtaskId: z.string().optional(),
    taskExpiryDate: z.string().optional(),
  }),
  parseParams: (params) => ({
    productId: z.string().parse(params.productId),
    batchId: z.string().parse(params.batchId),
    taskId: z.string().parse(params.taskId),
  }),
});

export const shrinkageRouteTree = shrinkageMainRoute.addChildren({
  requireWarehouseRoute: requireWarehouseRoute.addChildren({
    shrinkageWarehouseWrapperRoute: shrinkageWarehouseWrapperRoute.addChildren({
      shrinkageRoute,
      shrinkageBatchesRoute,
      removeFromInventoryRoute,
      removeFromInventoryByBatchRoute,
      shrinkItemsTaskRoute,
    }),
  }),
});
